// URL Builder Service
// Centralizes URL management for different environments

// Environment identifiers for deployed environments
const ENVIRONMENT_IDENTIFIERS = {
  dev: "plcbdh3rmq",
  qa: "tw233c22sq",
  // preprod uses the project number (deterministic Cloud Run URL), not a legacy hash
  preprod: "301201030704",
  prod: "ecqfa7qlxq",
} as const;

type Environment = "local" | "dev" | "qa" | "preprod" | "prod";

class UrlBuilder {
  private environment: Environment;
  private localBaseUrl: string;
  private devIdentifier: string;
  private qaIdentifier: string;
  private preprodIdentifier: string;
  private prodIdentifier: string;

  constructor() {
    this.environment = (import.meta.env.VITE_ENVIRONMENT as Environment) || "local";
    // For local development, use relative URL to leverage Vite proxy
    // This avoids CORS issues by proxying through the dev server
    // Default to dev environment for local development
    this.localBaseUrl = import.meta.env.VITE_LOCAL_BASE_URL || "/breaze-dev-firebase/europe-west1";
    this.devIdentifier = import.meta.env.VITE_DEV_IDENTIFIER || ENVIRONMENT_IDENTIFIERS.dev;
    this.qaIdentifier = import.meta.env.VITE_QA_IDENTIFIER || ENVIRONMENT_IDENTIFIERS.qa;
    this.preprodIdentifier =
      import.meta.env.VITE_PREPROD_IDENTIFIER || ENVIRONMENT_IDENTIFIERS.preprod;
    this.prodIdentifier = import.meta.env.VITE_PROD_IDENTIFIER || ENVIRONMENT_IDENTIFIERS.prod;
  }

  /**
   * Build URL for a given service based on current environment
   * @param serviceName - The service name (e.g., 'order-getOrders')
   * @returns The appropriate URL for the current environment
   */
  buildUrl(serviceName: string): string {
    // For local development, use the local base URL
    if (this.environment === "local") {
      return `${this.localBaseUrl}/${serviceName}`;
    }

    // For deployed environments, generate URL dynamically
    const lowerCaseServiceName = serviceName.toLowerCase();

    if (this.environment === "dev") {
      return `https://${lowerCaseServiceName}-${this.devIdentifier}-ew.a.run.app`;
    }

    if (this.environment === "qa") {
      return `https://${lowerCaseServiceName}-${this.qaIdentifier}-ew.a.run.app`;
    }

    if (this.environment === "preprod") {
      return `https://${lowerCaseServiceName}-${this.preprodIdentifier}.europe-west1.run.app`;
    }

    if (this.environment === "prod") {
      return `https://${lowerCaseServiceName}-${this.prodIdentifier}-ew.a.run.app`;
    }

    // Fallback: if environment is not recognized, warn and use local
    console.warn(`Unknown environment: ${this.environment}. Falling back to local.`);
    return `${this.localBaseUrl}/${serviceName}`;
  }

  /**
   * Get the current environment
   * @returns Current environment (local, dev, qa, prod)
   */
  getEnvironment(): Environment {
    return this.environment;
  }

  /**
   * Check if running in local environment
   * @returns true if local environment
   */
  isLocal(): boolean {
    return this.environment === "local";
  }

  /**
   * Get the environment identifier for the current environment
   * @returns Environment identifier (e.g., 'plcbdh3rmq' for DEV) or null
   */
  getEnvironmentIdentifier(): string | null {
    if (this.environment === "dev") return this.devIdentifier;
    if (this.environment === "qa") return this.qaIdentifier;
    if (this.environment === "preprod") return this.preprodIdentifier;
    if (this.environment === "prod") return this.prodIdentifier;
    return null;
  }

  /**
   * Update environment identifiers (useful when deployment IDs change)
   * @param environment - 'dev', 'qa' or 'prod'
   * @param identifier - New identifier
   */
  updateEnvironmentIdentifier(
    environment: "dev" | "qa" | "preprod" | "prod",
    identifier: string
  ): void {
    if (environment === "dev") {
      this.devIdentifier = identifier;
    } else if (environment === "qa") {
      this.qaIdentifier = identifier;
    } else if (environment === "preprod") {
      this.preprodIdentifier = identifier;
    } else if (environment === "prod") {
      this.prodIdentifier = identifier;
    }
  }
}

// Export singleton instance
export default new UrlBuilder();

// Also export the class for testing purposes
export { UrlBuilder };
